import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { getOverlayZIndex } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { MenuProvider } from '#pplx/frontend/aether/components/internal/menu/MenuContext.js';
import { MobileBottomSheetProvider } from '#pplx/frontend/aether/components/internal/menu/MobileBottomSheetContext.js';
import { NoopModal } from '#pplx/frontend/aether/components/internal/menu/NoopModal.js';
import { NOOP_MODAL_PROPS } from '#pplx/frontend/aether/components/internal/menu/noopModalProps.js';
import { useMenuFooterRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuFooterRegistry.js';
import { useMenuHeaderRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuHeaderRegistry.js';
import { useElevation, useNearestSurface, } from '#pplx/frontend/aether/components/Surface/useSurface.js';
import { useAetherLabels } from '#pplx/frontend/aether/internal/labels/useAetherLabels.js';
import { DeclarativeModal } from '#pplx/frontend/aether/modals/utils/DeclarativeModal.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
const CLOSE_BUTTON_CLEARANCE_CLASS_NAME = 'pt-[calc(var(--spacing-2)_+_2.5rem_+_var(--spacing-2))]';
/**
 * Generates the className string for the mobile bottom sheet overlay backdrop.
 */
function getBottomSheetOverlayClassName() {
    return classNames(
    // Position
    'fixed inset-0', 
    // Background color - semi-transparent black
    'bg-black/50', 
    // Restore pointer events so the overlay is clickable (Radix Dialog modal mode
    // sets pointer-events:none on the body which the overlay inherits)
    'pointer-events-auto', 
    // Enter animation - fade in when overlay appears
    'data-[state=open]:animate-fadeIn', 
    // Exit animation - fade out when overlay disappears
    'data-[state=closed]:animate-fadeOut');
}
/**
 * Generates the className string for the mobile bottom sheet content.
 */
function getBottomSheetContentClassName() {
    return classNames(
    // Background
    'bg-base', 
    // Position - anchored to bottom of screen
    'fixed inset-x-0 bottom-0', 
    // Layout
    'flex flex-col', 
    // Sizing - max 90% of viewport height
    'max-h-[90dvh]', 
    // Shadow
    'shadow-lg', 
    // Enter animation - slide up when sheet opens
    'data-[state=open]:animate-slideUp', 
    // Exit animation - slide down when sheet closes
    'data-[state=closed]:animate-slideDown', 
    // Rounded corners
    'rounded-t-xl', 
    // The sheet content is focus-managed by Radix and may receive focus when
    // opening; the container itself should not render a browser focus outline.
    'outline-hidden');
}
export function MobileBottomSheet({ isOpen, onToggle, restoreFocusElement, onCloseAutoFocus, triggerElement, preventOpenAutoFocus = false, title, children, }) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const portalTargetElement = useNearestSurface();
    const parentElevation = useElevation();
    const { close: closeLabel } = useAetherLabels();
    const handleClose = useCallback(() => onToggle(false), [onToggle]);
    const overlayZIndexStyle = useMemo(() => shouldUseActivationOrder ? undefined : { zIndex: getOverlayZIndex(parentElevation) }, [parentElevation, shouldUseActivationOrder]);
    const handleCloseAutoFocus = useCallback((event) => {
        onCloseAutoFocus?.(event);
        if (event.defaultPrevented)
            return;
        if (!restoreFocusElement?.isConnected) {
            return;
        }
        // Cancel the default focus restoration so we can redirect focus
        // to the caller-specified element instead of the trigger.
        event.preventDefault();
        restoreFocusElement.focus();
    }, [onCloseAutoFocus, restoreFocusElement]);
    const handleOpenAutoFocus = useCallback((event) => {
        // When requested, skip focusing the first focusable child on open
        // so opening the sheet does not show the mobile keyboard.
        event.preventDefault();
    }, []);
    const { footer, registry: footerRegistry } = useMenuFooterRegistry();
    const { header, registry: headerRegistry } = useMenuHeaderRegistry();
    return (_jsx(MobileBottomSheetProvider, { close: handleClose, children: _jsxs(Dialog.Root, { open: isOpen, onOpenChange: onToggle, children: [triggerElement != null && _jsx(Dialog.Trigger, { asChild: true, children: triggerElement }), _jsxs(Dialog.Portal, { container: shouldUseActivationOrder ? undefined : portalTargetElement, children: [_jsx(Dialog.Overlay, { className: getBottomSheetOverlayClassName(), style: overlayZIndexStyle, onClick: handleClose }), _jsxs(Dialog.Content, { className: getBottomSheetContentClassName(), style: overlayZIndexStyle, "aria-describedby": undefined, onOpenAutoFocus: preventOpenAutoFocus ? handleOpenAutoFocus : undefined, onCloseAutoFocus: handleCloseAutoFocus, children: [title ? (_jsx(Dialog.Title, { className: "px-4 pt-4 pr-12 shrink-0 truncate text-base font-semibold", title: title, children: title })) : (_jsx(VisuallyHidden, { children: _jsx(Dialog.Title, {}) })), header && (_jsx("div", { className: classNames('px-4 shrink-0', title ? 'pt-2' : CLOSE_BUTTON_CLEARANCE_CLASS_NAME), children: header })), _jsxs("div", { className: classNames('px-4 flex-1 overflow-y-auto', title || header ? 'pt-2' : CLOSE_BUTTON_CLEARANCE_CLASS_NAME, footer ? 'pb-2' : 'pb-[max(var(--safe-area-inset-bottom),_var(--spacing-8))]'), children: [_jsx(DeclarativeModal, { modal: NoopModal, props: NOOP_MODAL_PROPS }), _jsx(MenuProvider, { footerRegistry: footerRegistry, headerRegistry: headerRegistry, children: children })] }), footer && (_jsx("div", { className: "px-4 pb-[max(var(--safe-area-inset-bottom),_var(--spacing-8))]", children: footer })), _jsx("div", { className: "absolute top-2 right-2 z-10", children: _jsx(Dialog.Close, { asChild: true, children: _jsx(Button, { icon: $icon('x'), variant: "text", size: "default", rounded: true, "aria-label": closeLabel }) }) })] })] })] }) }));
}
//# sourceMappingURL=MobileBottomSheet.js.map